/**
 * WebSocket Hook for Real-Time Dashboard Updates
 * 
 * Provides real-time data updates via WebSocket connection with:
 * - Automatic reconnection
 * - Channel subscription management
 * - Event handling
 * - Connection status tracking
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// WebSocket connection URL — derived from REACT_APP_BACKEND_URL.
// Iter 60.4 — using replace('http','ws') replaces only the first occurrence,
// which is correct for "https://x" → "wss://x" and "http://x" → "ws://x".
// We anchor to the start of the string with a regex to be defensive against
// URLs that contain "http" later (e.g. as a path segment).
const WS_BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http(s?):/, 'ws$1:');

// Event types that can be received
export const EventTypes = {
  // FSM Events
  JOB_CREATED: 'job_created',
  JOB_UPDATED: 'job_updated',
  JOB_STATUS_CHANGED: 'job_status_changed',
  JOB_ASSIGNED: 'job_assigned',
  TECHNICIAN_LOCATION: 'technician_location',
  TECHNICIAN_STATUS: 'technician_status',
  ROUTE_OPTIMIZED: 'route_optimized',
  DISPATCH_RECOMMENDATION: 'dispatch_recommendation',
  
  // Sales Events
  LEAD_CREATED: 'lead_created',
  LEAD_UPDATED: 'lead_updated',
  LEAD_SCORED: 'lead_scored',
  DEAL_CREATED: 'deal_created',
  DEAL_UPDATED: 'deal_updated',
  DEAL_STAGE_CHANGED: 'deal_stage_changed',
  AI_RECOMMENDATION: 'ai_recommendation',
  
  // Service Events
  TICKET_CREATED: 'ticket_created',
  TICKET_UPDATED: 'ticket_updated',
  TICKET_ASSIGNED: 'ticket_assigned',
  TICKET_RESOLVED: 'ticket_resolved',
  SLA_ALERT: 'sla_alert',
  SENTIMENT_ALERT: 'sentiment_alert',
  
  // Global Events
  ALERT: 'alert',
  NOTIFICATION: 'notification',
  REFRESH: 'refresh',
  HEARTBEAT: 'heartbeat',
  CREDIT_THRESHOLD_CROSSED: 'credit_threshold_crossed',
};

// Channel types
export const Channels = {
  FSM: 'fsm',
  SALES: 'sales',
  SERVICE: 'service',
  GLOBAL: 'global',
  LOCATION: 'location',
};

/**
 * Custom hook for WebSocket connection and real-time updates
 * 
 * @param {string[]} channels - Array of channels to subscribe to
 * @param {Object} eventHandlers - Object mapping event types to handler functions
 * @param {Object} options - Additional options
 * @returns {Object} WebSocket state and methods
 */
export function useWebSocket(channels = ['global'], eventHandlers = {}, options = {}) {
  const {
    autoConnect = true,
    reconnectInterval = 5000,
    maxReconnectAttempts = 5,
    showToasts = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const handlersRef = useRef(eventHandlers);
  const reconnectAttemptsRef = useRef(0);
  // Stable channels key to avoid reconnect loops when Leads.js passes a fresh array each render
  const channelsKey = channels.join(',');
  const channelsRef = useRef(channelsKey);
  channelsRef.current = channelsKey;

  // Update handlers ref when they change
  useEffect(() => {
    handlersRef.current = eventHandlers;
  }, [eventHandlers]);

  // Get auth token (AuthContext uses 'access_token')
  const getToken = useCallback(() => {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const token = getToken();
    if (!token) {
      console.warn('No auth token available for WebSocket');
      return;
    }

    // Iter 60.4 — runtime safety guard: never attempt to connect with an
    // invalid base URL (e.g. localhost:443 or empty string). This prevents
    // the noisy "ws://localhost:443/ws" reconnection loop in browsers.
    if (!WS_BASE_URL || !/^wss?:\/\//.test(WS_BASE_URL) || WS_BASE_URL.includes('localhost')) {
      console.warn('[useWebSocket] Invalid or unsafe WS base URL — skipping connection:', WS_BASE_URL);
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* noop */ }
    }

    try {
      const wsUrl = `${WS_BASE_URL}/api/ws/connect?token=${token}&channels=${channelsRef.current}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);
        setError(null);
        if (showToasts) {
          toast.success('Real-time updates connected', { duration: 2000 });
        }
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        // Attempt reconnect if not intentional close AND under max attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          setReconnectAttempts(reconnectAttemptsRef.current);
          reconnectTimeoutRef.current = setTimeout(() => { connect(); }, reconnectInterval);
        }
      };

      wsRef.current.onerror = () => {
        setError('Connection error');
        setIsConnected(false);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setLastMessage(message);
          const { type, data, channel } = message;
          if (handlersRef.current[type]) {
            handlersRef.current[type](data, channel);
          }
          if (handlersRef.current.onMessage) {
            handlersRef.current.onMessage(message);
          }
          if (type === EventTypes.ALERT && showToasts) {
            toast.warning(data?.message || 'New alert received');
          }
          if (type === EventTypes.NOTIFICATION && showToasts) {
            toast.info(data?.message || 'New notification');
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
    } catch (e) {
      console.error('Failed to create WebSocket connection:', e);
      setError('Failed to connect');
    }
  }, [getToken, maxReconnectAttempts, reconnectInterval, showToasts]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Send a message
  const send = useCallback((action, data = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, data }));
      return true;
    }
    return false;
  }, []);

  // Subscribe to a channel
  const subscribe = useCallback((channel) => {
    return send('subscribe', { channel });
  }, [send]);

  // Unsubscribe from a channel
  const unsubscribe = useCallback((channel) => {
    return send('unsubscribe', { channel });
  }, [send]);

  // Send ping (keep-alive)
  const ping = useCallback(() => {
    return send('ping');
  }, [send]);

  // Auto-connect on mount / reconnect only when channels change.
  // Using channelsKey as the only re-trigger dep — `connect` and `disconnect` intentionally not included.
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  connectRef.current = connect;
  disconnectRef.current = disconnect;
  useEffect(() => {
    if (autoConnect) {
      connectRef.current();
    }
    return () => {
      disconnectRef.current();
    };
  }, [autoConnect, channelsKey]);

  // Ping every 25 seconds to keep connection alive
  useEffect(() => {
    if (!isConnected) return;
    
    const pingInterval = setInterval(() => {
      ping();
    }, 25000);
    
    return () => clearInterval(pingInterval);
  }, [isConnected, ping]);

  return {
    isConnected,
    lastMessage,
    error,
    reconnectAttempts,
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
    ping,
  };
}

/**
 * Hook specifically for FSM Dashboard real-time updates
 */
export function useFSMWebSocket(onUpdate) {
  const eventHandlers = {
    [EventTypes.JOB_CREATED]: (data) => {
      toast.info(`New job created: ${data?.job_number}`);
      onUpdate?.('job_created', data);
    },
    [EventTypes.JOB_UPDATED]: (data) => {
      onUpdate?.('job_updated', data);
    },
    [EventTypes.JOB_STATUS_CHANGED]: (data) => {
      toast.info(`Job ${data?.job_number} status: ${data?.status}`);
      onUpdate?.('job_status_changed', data);
    },
    [EventTypes.JOB_ASSIGNED]: (data) => {
      toast.success(`Job ${data?.job_number} assigned to ${data?.technician_name}`);
      onUpdate?.('job_assigned', data);
    },
    [EventTypes.TECHNICIAN_LOCATION]: (data) => {
      onUpdate?.('technician_location', data);
    },
    [EventTypes.TECHNICIAN_STATUS]: (data) => {
      onUpdate?.('technician_status', data);
    },
    [EventTypes.DISPATCH_RECOMMENDATION]: (data) => {
      toast.info('New dispatch recommendation available');
      onUpdate?.('dispatch_recommendation', data);
    },
  };

  return useWebSocket([Channels.FSM, Channels.LOCATION, Channels.GLOBAL], eventHandlers);
}

/**
 * Hook specifically for Sales Dashboard real-time updates
 */
export function useSalesWebSocket(onUpdate) {
  const eventHandlers = {
    [EventTypes.LEAD_CREATED]: (data) => {
      toast.info(`New lead: ${data?.name}`);
      onUpdate?.('lead_created', data);
    },
    [EventTypes.LEAD_SCORED]: (data) => {
      onUpdate?.('lead_scored', data);
    },
    [EventTypes.DEAL_CREATED]: (data) => {
      toast.info(`New deal: ${data?.name}`);
      onUpdate?.('deal_created', data);
    },
    [EventTypes.DEAL_STAGE_CHANGED]: (data) => {
      toast.info(`Deal ${data?.name} moved to ${data?.stage}`);
      onUpdate?.('deal_stage_changed', data);
    },
    [EventTypes.AI_RECOMMENDATION]: (data) => {
      toast.info('New AI recommendation');
      onUpdate?.('ai_recommendation', data);
    },
  };

  return useWebSocket([Channels.SALES, Channels.GLOBAL], eventHandlers);
}

/**
 * Hook specifically for Service Dashboard real-time updates
 */
export function useServiceWebSocket(onUpdate) {
  const eventHandlers = {
    [EventTypes.TICKET_CREATED]: (data) => {
      toast.info(`New ticket: ${data?.ticket_number}`);
      onUpdate?.('ticket_created', data);
    },
    [EventTypes.TICKET_UPDATED]: (data) => {
      onUpdate?.('ticket_updated', data);
    },
    [EventTypes.TICKET_ASSIGNED]: (data) => {
      toast.success(`Ticket ${data?.ticket_number} assigned`);
      onUpdate?.('ticket_assigned', data);
    },
    [EventTypes.TICKET_RESOLVED]: (data) => {
      toast.success(`Ticket ${data?.ticket_number} resolved`);
      onUpdate?.('ticket_resolved', data);
    },
    [EventTypes.SLA_ALERT]: (data) => {
      toast.warning(`SLA Alert: ${data?.message}`);
      onUpdate?.('sla_alert', data);
    },
    [EventTypes.SENTIMENT_ALERT]: (data) => {
      toast.warning('Negative sentiment detected');
      onUpdate?.('sentiment_alert', data);
    },
  };

  return useWebSocket([Channels.SERVICE, Channels.GLOBAL], eventHandlers);
}

export default useWebSocket;
